// @ts-strict-ignore
import { useMutation } from "@apollo/client";
import ActionDialog from "@dashboard/components/ActionDialog";
import { Select } from "@dashboard/components/Select";
import useNotifier from "@dashboard/hooks/useNotifier";
import { TextField } from "@material-ui/core";
import { Box, Option } from "@saleor/macaw-ui-next";
import React, { useState } from "react";

import CmsToggle from "../../cms/components/CmsToggle";
import { SDUI_CONFIG_CREATE_MUTATION, SDUI_CONFIG_UPDATE_MUTATION } from "../sduiMutations";
import { SDUI_CONFIGS_QUERY } from "../sduiQueries";
import { SDUIConfig } from "../types";

interface ConfigDialogProps {
  open: boolean;
  config?: SDUIConfig | null;
  onClose: () => void;
}

const PLATFORM_OPTIONS: Option[] = [
  { label: "All Platforms", value: "ALL" },
  { label: "Android", value: "ANDROID" },
  { label: "iOS", value: "IOS" },
  { label: "Web Storefront", value: "WEB" },
];

export const ConfigDialog: React.FC<ConfigDialogProps> = ({ open, config, onClose }) => {
  const notify = useNotifier();
  const isEdit = !!config;

  const [key, setKey] = useState(config?.key || "");
  const [label, setLabel] = useState(config?.label || "");
  const [description, setDescription] = useState(config?.description || "");
  const [platform, setPlatform] = useState<string>(config?.platform || "ALL");
  const [priority, setPriority] = useState(config ? config.priority.toString() : "0");
  const [configData, setConfigData] = useState(
    config?.configData
      ? typeof config.configData === "string"
        ? config.configData
        : JSON.stringify(config.configData, null, 2)
      : JSON.stringify({ enabled: true, value: "custom" }, null, 2)
  );
  const [isPublished, setIsPublished] = useState(config ? config.isPublished : true);

  React.useEffect(() => {
    if (config) {
      setKey(config.key || "");
      setLabel(config.label || "");
      setDescription(config.description || "");
      setPlatform(config.platform || "ALL");
      setPriority(config.priority !== undefined && config.priority !== null ? config.priority.toString() : "0");
      setConfigData(
        config.configData
          ? typeof config.configData === "string"
            ? config.configData
            : JSON.stringify(config.configData, null, 2)
          : JSON.stringify({ enabled: true, value: "custom" }, null, 2)
      );
      setIsPublished(config.isPublished ?? true);
    } else {
      setKey("");
      setLabel("");
      setDescription("");
      setPlatform("ALL");
      setPriority("0");
      setConfigData(JSON.stringify({ enabled: true, value: "custom" }, null, 2));
      setIsPublished(true);
    }
  }, [config, open]);

  const [createConfig, { loading: creating }] = useMutation(SDUI_CONFIG_CREATE_MUTATION, {
    refetchQueries: [{ query: SDUI_CONFIGS_QUERY }],
    onCompleted: data => {
      if (data.sduiConfigCreate?.errors?.length) {
        notify({ status: "error", text: data.sduiConfigCreate.errors[0].message });
      } else {
        notify({ status: "success", text: "Remote config parameter created" });
        onClose();
      }
    },
  });

  const [updateConfig, { loading: updating }] = useMutation(SDUI_CONFIG_UPDATE_MUTATION, {
    refetchQueries: [{ query: SDUI_CONFIGS_QUERY }],
    onCompleted: data => {
      if (data.sduiConfigUpdate?.errors?.length) {
        notify({ status: "error", text: data.sduiConfigUpdate.errors[0].message });
      } else {
        notify({ status: "success", text: "Remote config parameter updated" });
        onClose();
      }
    },
  });

  const handleSubmit = () => {
    if (!key || !label) {
      notify({ status: "error", text: "Key and label are required" });
      return;
    }

    try {
      JSON.parse(configData);
    } catch {
      notify({ status: "error", text: "Invalid JSON in config data" });
      return;
    }

    const input: any = {
      key,
      label,
      description,
      platform,
      priority: parseInt(priority, 10) || 0,
      configData,
      isPublished,
    };

    if (isEdit) {
      updateConfig({ variables: { id: config.id, input } });
    } else {
      createConfig({ variables: { input } });
    }
  };

  return (
    <ActionDialog
      open={open}
      size="md"
      title={isEdit ? "Edit Remote Config" : "Add Remote Config Parameter"}
      confirmButtonState={creating || updating ? "loading" : "default"}
      onClose={onClose}
      onConfirm={handleSubmit}
    >
      <Box display="flex" flexDirection="column" gap={4} width="100%">
        <TextField
          label="Parameter Key"
          fullWidth
          value={key}
          onChange={e => setKey(e.target.value)}
          disabled={isEdit}
          helperText="e.g. enable_festival_fireworks, min_free_delivery_banner, max_cart_qty"
          required
        />
        <TextField
          label="Display Label"
          fullWidth
          value={label}
          onChange={e => setLabel(e.target.value)}
          required
        />
        <TextField
          label="Description / Purpose"
          fullWidth
          value={description}
          onChange={e => setDescription(e.target.value)}
        />

        <Select
          label="Client Platform"
          name="platform"
          value={platform}
          options={PLATFORM_OPTIONS}
          onChange={({ target }) => setPlatform(target.value)}
        />

        <TextField
          label="Priority (Higher overrides lower)"
          fullWidth
          type="number"
          value={priority}
          onChange={e => setPriority(e.target.value)}
        />
        <TextField
          label="Config Data (JSON Object / Values)"
          fullWidth
          multiline
          rows={5}
          value={configData}
          onChange={e => setConfigData(e.target.value)}
          helperText="JSON dictionary containing feature flags, string keys, numbers"
        />

        <CmsToggle
          label="Active & Deployed to Clients"
          description="Make this remote configuration active for mobile app and web clients"
          checked={isPublished}
          onChange={setIsPublished}
        />
      </Box>
    </ActionDialog>
  );
};

export default ConfigDialog;
